import { ShoppingBagIcon } from 'lucide-react';
import { useMemo } from 'react';

import { OrderStatus, type OrderDTO } from '@invenco/common-interface/sales';

import { Header, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { StatusBadge } from '@/components/status-badge';
import { useRequiredParam } from '@/shared/hooks/use-required-param';
import { orderStatusType } from '@/shared/status-maps';
import { orderStatusTitle } from '@/shared/title-maps';

import { EditableOrderLinesCard, OrderLinesCard } from '../order-lines-card';
import type { OrderLinesOrderContext } from '../order-lines-card/order-totals';
import { OrderActions } from './order-actions';
import { OrderDetailTabs } from './order-detail-tabs';
import { useOrder } from './use-order';

const getOrderContext = (order: OrderDTO | undefined): OrderLinesOrderContext => ({
  currency: order?.currency,
  channelId: order?.channelId,
  shippingProfileName: order?.shippingProfileName,
  subtotalPrice: order?.subtotalPrice,
  subtotalDiscounts: order?.subtotalDiscounts,
  subtotalShipping: order?.subtotalShipping,
  totalPrice: order?.totalPrice,
});

export function OrderDetailsShad() {
  const id = useRequiredParam('id');
  const {
    order,
    shipments,
    isLoading,
    refetchOrder,
    addOrderLine,
    updateOrderLine,
    applyManualHold,
    releaseManualHold,
    revertToDraft,
    releaseOrder,
    cancelOrder,
    closeOrder,
  } = useOrder(id);
  const orderContext = useMemo(() => getOrderContext(order), [order]);
  const lines = order?.orderLines ?? [];
  const isEditable = order?.status === OrderStatus.DRAFT;

  return (
    <Page>
      <Header loading={isLoading}>
        <HeaderIcon parentUrl="/sales/orders" parentTitle="Orders">
          <ShoppingBagIcon />
        </HeaderIcon>
        <HeaderTitle pageTitle={order?.name} className="flex flex-wrap items-center gap-2">
          {order?.name}
          {order?.status ? (
            <StatusBadge status={order.status} type={orderStatusType} title={orderStatusTitle} />
          ) : null}
        </HeaderTitle>
        <OrderActions
          order={order}
          isLoading={isLoading}
          onRefresh={refetchOrder}
          applyManualHold={applyManualHold}
          releaseManualHold={releaseManualHold}
          revertToDraft={revertToDraft}
          releaseOrder={releaseOrder}
          cancelOrder={cancelOrder}
          closeOrder={closeOrder}
        />
      </Header>
      <PageContent variant="split">
        <DetailMain>
          {isEditable ? (
            <EditableOrderLinesCard
              title="Items"
              lines={lines}
              order={orderContext}
              showTotals
              isLoading={isLoading}
              initialVisibleLineCount={5}
              onAddLine={addOrderLine}
              onUpdateLine={updateOrderLine}
            />
          ) : (
            <OrderLinesCard
              title="Items"
              lines={lines}
              order={orderContext}
              showTotals
              isLoading={isLoading}
              initialVisibleLineCount={5}
            />
          )}
        </DetailMain>
        <DetailSide>
          <OrderDetailTabs order={order} shipments={shipments} isLoading={isLoading} />
        </DetailSide>
      </PageContent>
    </Page>
  );
}
